import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Modal,
    Paper,
    Button,
    Grid,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MessageActions } from './MessageActions';

interface DocumentMessageComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

function extractFileNameFromUrl(url: string): string | null {
    try {
        const urlWithoutParams = url.split('?')[0]; // remove query params
        const segments = urlWithoutParams.split('/');
        const filename = segments[segments.length - 1];
        return filename.endsWith('.pdf') ? filename : null;
    } catch (err) {
        console.error('Invalid URL:', err);
        return null;
    }
}

export const DocumentMessageComponent: React.FC<DocumentMessageComponentProps> = ({
    message,
    isCurrentUser,
    onReactionClick = () => {},
    onDeleteMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);
    const [viewerType, setViewerType] = useState<'google' | 'native' | 'mozilla'>('google');
    const [isViewerLoading, setIsViewerLoading] = useState(false);

    // Ensure documents is always an array
    const documents = message.document || [];

    // Determine document details
    const selectedDocumentUrl = documents[selectedDocumentIndex] || '';
    const selectedFileName = extractFileNameFromUrl(selectedDocumentUrl) || 'Document';
    const isPdf = !!extractFileNameFromUrl(selectedDocumentUrl);

    const handleOpenModal = (index: number) => {
        setSelectedDocumentIndex(index);
        setModalOpen(true);
        setIsViewerLoading(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setViewerType('google');
    };

    const toggleViewerType = () => {
        setIsViewerLoading(true);
        if (viewerType === 'google') {
            setViewerType('native');
        } else if (viewerType === 'native') {
            setViewerType('mozilla');
        } else {
            setViewerType('google');
        }
    };

    const handleViewerLoad = () => {
        setIsViewerLoading(false);
    };

    // Get the appropriate viewer URL based on the selected type
    const getViewerUrl = () => {
        switch (viewerType) {
            case 'google':
                return `https://docs.google.com/viewer?url=${encodeURIComponent(selectedDocumentUrl)}&embedded=true`;
            case 'mozilla':
                // Using Mozilla's PDF.js viewer
                return `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(
                    selectedDocumentUrl,
                )}`;
            case 'native':
                // Direct browser PDF viewing
                return selectedDocumentUrl;
            default:
                return `https://docs.google.com/viewer?url=${encodeURIComponent(selectedDocumentUrl)}&embedded=true`;
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: { xs: '100%', sm: '280px' },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundColor: theme[themeStyle].deep_grey,
                    border: `1px solid ${theme[themeStyle].grey}30`,
                    p: 2,
                    overflowX: 'auto',
                }}>
                {/* Show multiple documents in a grid if there are more than one */}
                <Grid container spacing={2}>
                    {documents.map((documentUrl, index) => {
                        const fileName = extractFileNameFromUrl(documentUrl) || `Document ${index + 1}`;
                        const docIsPdf = !!extractFileNameFromUrl(documentUrl);

                        return (
                            <Grid item xs={documents.length > 1 ? 6 : 12} key={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <DescriptionIcon
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            fontSize: documents.length > 1 ? 30 : 40,
                                            mr: 1,
                                        }}
                                    />

                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: documents.length > 1 ? '14px' : '16px',
                                                color: theme[themeStyle].tag_white,
                                                mb: 0.5,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {fileName}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '12px',
                                                color: theme[themeStyle].grey,
                                            }}>
                                            {docIsPdf ? 'PDF Document' : 'Document'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Preview button for each document */}
                                <Button
                                    variant="contained"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleOpenModal(index)}
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].true_white,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: documents.length > 1 ? '12px' : '14px',
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].highlight + 'CC',
                                        },
                                        width: '100%',
                                        mt: 1,
                                        p: documents.length > 1 ? '4px 8px' : '6px 8px',
                                    }}>
                                    Preview
                                </Button>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Message content display */}
                {message.message_content && (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '14px',
                            color: theme[themeStyle].tag_white,
                            mt: 2,
                            mb: 1,
                        }}>
                        {message.message_content}
                    </Typography>
                )}

                {/* Add message info footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        {formatMessageTime(message.createdAt)}
                    </Typography>

                    {/* Add message actions */}
                    <MessageActions
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={onDeleteMessage}
                    />
                </Box>
            </Box>

            {/* Document Viewer Modal */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <Paper
                    sx={{
                        width: isSmallScreen ? '100%' : '90%',
                        maxWidth: isSmallScreen ? '100%' : '900px',
                        height: isSmallScreen ? '100vh' : '80vh',
                        borderRadius: isSmallScreen ? 0 : 2,
                        bgcolor: theme[themeStyle].deep_grey,
                        boxShadow: 24,
                        p: 0,
                        position: 'relative',
                        overflow: 'hidden',
                        m: isSmallScreen ? 0 : 'auto',
                    }}>
                    <Box
                        sx={{
                            p: isSmallScreen ? 1 : 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme[themeStyle].grey}30`,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].tag_white,
                                fontSize: isSmallScreen ? '14px' : '16px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: isSmallScreen ? '60%' : '80%',
                            }}>
                            {selectedFileName}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isPdf && (
                                <Tooltip title="Change viewer (if preview isn't working)">
                                    <IconButton
                                        onClick={toggleViewerType}
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            mr: 1,
                                            padding: isSmallScreen ? '4px' : '8px',
                                        }}>
                                        <RefreshIcon fontSize={isSmallScreen ? 'small' : 'medium'} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton
                                onClick={handleCloseModal}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    padding: isSmallScreen ? '4px' : '8px',
                                }}>
                                <CloseIcon fontSize={isSmallScreen ? 'small' : 'medium'} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* PDF Viewer with loading indicator */}
                    {isPdf && (
                        <Box sx={{ position: 'relative', height: 'calc(100% - 56px)' }}>
                            {isViewerLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        zIndex: 2,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            mb: 2,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        Loading PDF...
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme[themeStyle].grey,
                                            textAlign: 'center',
                                            maxWidth: '80%',
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        If the PDF doesn't load, try changing the viewer using the refresh button above
                                    </Typography>
                                </Box>
                            )}
                            <iframe
                                src={getViewerUrl()}
                                width="100%"
                                height={isSmallScreen ? 'calc(100vh - 42px)' : '100%'}
                                style={{ border: 'none' }}
                                title="PDF Viewer"
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                onLoad={handleViewerLoad}
                            />
                        </Box>
                    )}

                    {/* Non-PDF Document (fallback to download link) */}
                    {!isPdf && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: isSmallScreen ? 'calc(100vh - 42px)' : 'calc(100% - 56px)',
                                p: isSmallScreen ? 2 : 3,
                            }}>
                            <DescriptionIcon
                                sx={{
                                    fontSize: isSmallScreen ? 60 : 80,
                                    color: theme[themeStyle].highlight,
                                    mb: 2,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: isSmallScreen ? '16px' : '18px',
                                    color: theme[themeStyle].tag_white,
                                    mb: 2,
                                    textAlign: 'center',
                                }}>
                                This document type cannot be previewed
                            </Typography>

                            <Button
                                variant="contained"
                                href={selectedDocumentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].true_white,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: isSmallScreen ? '14px' : '16px',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].highlight + 'CC',
                                    },
                                    mt: 2,
                                }}>
                                Download Document
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Modal>
        </>
    );
};
